import MediaAssetPicker from '../../components/settings/MediaAssetPicker';
import { experiencePath, storefrontSiteUrl } from '../../services/storefrontExperience';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Puck, createUsePuck } from '@puckeditor/core';
import '@puckeditor/core/puck.css';
import './PageBuilder.css';

import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { useSnackbar } from 'notistack';
import NotificationSnackbar from '../../components/ui/NotificationSnackbar';
import CrmButton from '../../components/ui/CrmButton';
import { CrmInput, CrmSelect, CrmSwitch, CrmTextarea } from '../../components/ui/CrmFormControls';
import { useStorefrontCms } from '../../services/fusionBoxCmsService';
import { defaultPropsForModule, getFusionBoxPuckConfig, moduleLabels } from './puck/fusionBoxPuckConfig';

const usePuck = createUsePuck();

const configuredFrontendUrl = process.env.REACT_APP_FRONTEND_URL || process.env.REACT_APP_SITE_URL || '';
const isLocalDashboard = ['localhost', '127.0.0.1'].includes(window.location.hostname);
const configuredUrlIsLocal = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(configuredFrontendUrl);
const LEGACY_FRONTEND_URL = configuredFrontendUrl && (isLocalDashboard || !configuredUrlIsLocal)
  ? configuredFrontendUrl
  : isLocalDashboard
    ? 'http://localhost:4000'
    : 'https://thefusionbox.co';
const frontendUrl = page => page?.storefront_code === 'FUSION_BOX_WEB'
  ? LEGACY_FRONTEND_URL : storefrontSiteUrl({ domain: page?.storefront_domain });
const frontendOrigin = page => { try { return new URL(frontendUrl(page)).origin; } catch { return ''; } };
const ROOT_ZONE = 'root:default-zone';
const PREVIEW_UPDATE_MESSAGE = 'FUSION_BOX_BUILDER_UPDATE';
const PREVIEW_READY_MESSAGE = 'FUSION_BOX_BUILDER_READY';
const PREVIEW_SELECT_MESSAGE = 'FUSION_BOX_BUILDER_SELECT_SECTION';
const BUILDER_VIEWPORT_WIDTHS = {
  desktop: 1440,
  tablet: 768,
  mobile: 390,
};

const buildBuilderPreviewUrl = (page) => {
  const slug = String(page?.slug || 'home').replace(/^\/+|\/+$/g, '') || 'home';
  const encodedSlug = slug.split('/').map((part) => encodeURIComponent(part)).join('/');
  const url = frontendUrl(page);
  return url ? `${url.replace(/\/$/, '')}/preview/${encodedSlug}?builder=1&storefront=${encodeURIComponent(page?.storefront_code || '')}` : '';
};

const slugify = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/['"]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'page';

const makeSectionId = (id) => `cms-section-${id}`;
const parseSectionId = (id) => {
  const match = String(id || '').match(/^cms-section-(\d+)$/);
  return match ? Number(match[1]) : null;
};

const isActive = (value) => value !== false && value !== 0 && value !== '0' && value !== 'false' && value !== 'hidden';

const stripEditorProps = (props = {}) => {
  const clean = { ...props };
  delete clean.id;
  delete clean.section_label;
  delete clean.section_status;
  return clean;
};

const normalizeSectionToPuckItem = (section) => {
  const moduleType = section.module_type || 'rich_text';
  const defaults = defaultPropsForModule(moduleType);
  const content = section.content || {};

  return {
    type: moduleType,
    props: {
      ...defaults,
      ...content,
      id: makeSectionId(section.id),
      section_label: section.label || defaults.section_label || moduleLabels[moduleType] || moduleType,
      section_status: isActive(section.is_active) ? 'active' : 'hidden',
      style: {
        ...(defaults.style || {}),
        ...(content.style || {}),
      },
    },
  };
};

const buildPuckData = (sections = [], page = {}) => ({
  content: sections.map(normalizeSectionToPuckItem),
  root: { props: {} },
  zones: {},
});

const getLabelForItem = (item) => item?.props?.section_label || moduleLabels[item?.type] || item?.type || 'Section';

const buildLivePreviewData = (data, page) => ({
  page: {
    ...(page || {}),
    slug: page?.slug || 'home',
  },
  sections: (Array.isArray(data?.content) ? data.content : []).map((item, index) => ({
    id: item?.props?.id || `builder-section-${index}`,
    section_key: item?.props?.id || `${item?.type || 'section'}-${index}`,
    module_type: item?.type || 'rich_text',
    label: getLabelForItem(item),
    is_active: isActive(item?.props?.section_status) ? 1 : 0,
    display_order: (index + 1) * 10,
    content: stripEditorProps(item?.props || {}),
  })),
});

const sendLivePreviewData = (frame, data, page, selectedSectionId = '') => {
  if (!frame?.contentWindow || !frontendOrigin(page)) return;
  frame.contentWindow.postMessage({
    type: PREVIEW_UPDATE_MESSAGE,
    version: 1,
    payload: buildLivePreviewData(data, page),
    selectedSectionId,
  }, frontendOrigin(page));
};

function ClickToAddDrawerItem({ name, children }) {
  const contentLength = usePuck((state) => (Array.isArray(state.appState?.data?.content) ? state.appState.data.content.length : 0));
  const dispatch = usePuck((state) => state.dispatch);

  const handleAdd = () => {
    const destinationIndex = contentLength;
    dispatch({
      type: 'insert',
      componentType: name,
      destinationIndex,
      destinationZone: ROOT_ZONE,
      recordHistory: true,
    });
    dispatch({
      type: 'setUi',
      ui: { itemSelector: { index: destinationIndex, zone: ROOT_ZONE } },
    });
  };

  return (
    <button type="button" className="fb-puck-add-module" onClick={handleAdd} title={`Add ${moduleLabels[name] || name}`}>
      {children}
      <span className="fb-puck-add-module__plus" aria-hidden="true">+</span>
    </button>
  );
}

function NuxtLivePreviewCanvas({ page }) {
  const frameRef = useRef(null);
  const previewReadyRef = useRef(false);
  const stageRef = useRef(null);
  const puckData = usePuck((state) => state.appState?.data);
  const dispatch = usePuck((state) => state.dispatch);
  const getSelectorForId = usePuck((state) => state.getSelectorForId);
  const selectedItem = usePuck((state) => state.selectedItem);
  const [viewport, setViewport] = useState('desktop');
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const previewUrl = useMemo(() => buildBuilderPreviewUrl(page), [page?.slug, page?.storefront_domain, page?.storefront_code]);
  const selectedSectionId = selectedItem?.props?.id || '';
  const viewportWidth = BUILDER_VIEWPORT_WIDTHS[viewport];
  const viewportScale = stageSize.width ? Math.min(1, stageSize.width / viewportWidth) : 1;
  const scaledViewportWidth = viewportWidth * viewportScale;
  const viewportHeight = stageSize.height && viewportScale ? stageSize.height / viewportScale : 0;

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;

    const updateStageSize = () => {
      const styles = window.getComputedStyle(stage);
      const horizontalPadding = parseFloat(styles.paddingLeft || 0) + parseFloat(styles.paddingRight || 0);
      const verticalPadding = parseFloat(styles.paddingTop || 0) + parseFloat(styles.paddingBottom || 0);
      const nextWidth = Math.max(0, stage.clientWidth - horizontalPadding);
      const nextHeight = Math.max(0, stage.clientHeight - verticalPadding);

      setStageSize((current) => (
        current.width === nextWidth && current.height === nextHeight
          ? current
          : { width: nextWidth, height: nextHeight }
      ));
    };

    updateStageSize();
    window.addEventListener('resize', updateStageSize);

    const ResizeObserverConstructor = window.ResizeObserver;
    const resizeObserver = typeof ResizeObserverConstructor === 'undefined'
      ? null
      : new ResizeObserverConstructor(updateStageSize);
    resizeObserver?.observe(stage);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateStageSize);
    };
  }, []);

  const pushUpdate = useCallback(() => {
    if (!previewReadyRef.current) return;
    sendLivePreviewData(frameRef.current, puckData, page, selectedSectionId);
  }, [puckData, page, selectedSectionId]);

  useEffect(() => {
    const timer = setTimeout(pushUpdate, 60);
    return () => clearTimeout(timer);
  }, [pushUpdate]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== frontendOrigin(page) || event.source !== frameRef.current?.contentWindow) return;

      if (event.data?.type === PREVIEW_READY_MESSAGE) {
        previewReadyRef.current = true;
        sendLivePreviewData(frameRef.current, puckData, page, selectedSectionId);
        return;
      }

      if (event.data?.type === PREVIEW_SELECT_MESSAGE) {
        const selector = getSelectorForId(String(event.data.sectionId || ''));
        if (selector) dispatch({ type: 'setUi', ui: { itemSelector: selector } });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [puckData, dispatch, getSelectorForId, page, selectedSectionId]);

  const moveSelected = (direction) => {
    const selector = selectedSectionId ? getSelectorForId(selectedSectionId) : null;
    if (!selector) return;
    const contentLength = Array.isArray(puckData?.content) ? puckData.content.length : 0;
    const destinationIndex = Math.max(0, Math.min(contentLength - 1, selector.index + direction));
    if (destinationIndex === selector.index) return;
    dispatch({
      type: 'reorder',
      sourceIndex: selector.index,
      destinationIndex,
      destinationZone: selector.zone || ROOT_ZONE,
      recordHistory: true,
    });
    dispatch({
      type: 'setUi',
      ui: { itemSelector: { index: destinationIndex, zone: selector.zone || ROOT_ZONE } },
    });
  };

  return (
    <div className="fb-nuxt-preview-canvas">
      <div className="fb-nuxt-preview-toolbar">
        <div>
          <strong>Live Nuxt preview</strong>
          <span>Exact frontend components and Tailwind styles</span>
        </div>
        <div className="fb-nuxt-preview-toolbar__actions">
          {selectedSectionId ? (
            <>
              <button type="button" onClick={() => moveSelected(-1)}>Move up</button>
              <button type="button" onClick={() => moveSelected(1)}>Move down</button>
            </>
          ) : null}
          <button type="button" className={viewport === 'desktop' ? 'is-active' : ''} onClick={() => setViewport('desktop')}>Desktop</button>
          <button type="button" className={viewport === 'tablet' ? 'is-active' : ''} onClick={() => setViewport('tablet')}>Tablet</button>
          <button type="button" className={viewport === 'mobile' ? 'is-active' : ''} onClick={() => setViewport('mobile')}>Mobile</button>
        </div>
      </div>
      <div ref={stageRef} className="fb-nuxt-preview-stage">
        <div
          className="fb-nuxt-preview-viewport-shell"
          style={{
            width: scaledViewportWidth,
            height: stageSize.height || '100%',
          }}
        >
          <div
            className="fb-nuxt-preview-viewport"
            style={{
              width: viewportWidth,
              height: viewportHeight || '100%',
              transform: `scale(${viewportScale})`,
            }}
          >
            <iframe
              ref={frameRef}
              title="Live Nuxt page builder preview"
              src={previewUrl}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function PageBuilder() {
  const { createCmsPageSection, deleteCmsPage, deleteCmsPageSection, fetchCmsPage, updateCmsPage, updateCmsPageSection } = useStorefrontCms();
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [puckData, setPuckData] = useState({ content: [], root: { props: {} }, zones: {} });
  const [editorVersion, setEditorVersion] = useState(0);
  const [error, setError] = useState('');
  const [pageSettingsOpen, setPageSettingsOpen] = useState(false);
  const [notify, setNotify] = useState({ open: false, message: '', severity: 'success' });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewViewport, setPreviewViewport] = useState('desktop');
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const previewFrameRef = useRef(null);
  const previewFrameReadyRef = useRef(false);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchCmsPage(pageId);
      const pageData = data.page || null;
      const sectionData = Array.isArray(data.sections) ? data.sections : [];
      setPage(pageData);
      setSections(sectionData);
      setPuckData(buildPuckData(sectionData, pageData || {}));
      setEditorVersion((version) => version + 1);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load page');
    } finally {
      setLoading(false);
    }
  }, [pageId]);

  const puckConfig = useMemo(() => getFusionBoxPuckConfig(page?.slug), [page?.slug, page?.storefront_domain, page?.storefront_code]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const previewFrameUrl = useMemo(() => {
    if (!page) return '';
    return buildBuilderPreviewUrl(page);
  }, [page]);

  const viewportWidths = { desktop: '100%', tablet: 768, mobile: 375 };

  const openPreview = useCallback(() => {
    setPreviewOpen(true);
  }, []);

  const sendDialogPreviewData = useCallback(() => {
    if (!previewFrameReadyRef.current) return;
    sendLivePreviewData(previewFrameRef.current, puckData, page);
  }, [page, puckData]);

  useEffect(() => {
    previewFrameReadyRef.current = false;
  }, [previewFrameUrl, previewOpen, previewRefreshKey]);

  useEffect(() => {
    if (!previewOpen) return undefined;
    const timer = setTimeout(sendDialogPreviewData, 60);
    return () => clearTimeout(timer);
  }, [previewOpen, previewRefreshKey, sendDialogPreviewData]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.origin === frontendOrigin(page)
        && event.source === previewFrameRef.current?.contentWindow
        && event.data?.type === PREVIEW_READY_MESSAGE
      ) {
        previewFrameReadyRef.current = true;
        sendLivePreviewData(previewFrameRef.current, puckData, page);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [page, puckData]);

  const puckOverrides = useMemo(() => ({
    preview: () => <NuxtLivePreviewCanvas page={page} />,
    drawerItem: ClickToAddDrawerItem,
  }), [page]);

  const pageStatusChip = useMemo(() => {
    if (!page) return null;
    return page.status === 'published'
      ? <Chip label="Published" size="small" color="success" />
      : <Chip label="Draft" size="small" color="warning" />;
  }, [page]);

  const savePageSettings = async () => {
    if (!page) return;
    try {
      const payload = {
        title: page.title,
        slug: page.slug,
        status: page.status,
        seo_title: page.seo_title,
        seo_description: page.seo_description,
        og_title: page.og_title,
        og_description: page.og_description,
        og_image_url: page.og_image_url,
        canonical_url: page.canonical_url,
        robots: page.robots,
        schema_json: page.schema_json,
        show_header: page.show_header ? 1 : 0,
        show_footer: page.show_footer ? 1 : 0,
      };
      const updated = await updateCmsPage(page.id, payload);
      setPage(current => ({ ...current, ...updated }));
      enqueueSnackbar('Page settings saved', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || 'Failed to save page settings', { variant: 'error' });
    }
  };

  const syncSectionsFromPuck = async (data) => {
    const existingIds = new Set(sections.map((section) => Number(section.id)));
    const seenExistingIds = new Set();
    const contentItems = Array.isArray(data?.content) ? data.content : [];

    for (let index = 0; index < contentItems.length; index += 1) {
      const item = contentItems[index];
      const sectionId = parseSectionId(item?.props?.id);
      const moduleType = item?.type;
      const label = getLabelForItem(item);
      const active = isActive(item?.props?.section_status) ? 1 : 0;
      const displayOrder = (index + 1) * 10;
      const content = stripEditorProps(item?.props || {});

      if (!moduleType) continue;

      if (sectionId && existingIds.has(sectionId)) {
        seenExistingIds.add(sectionId);
        await updateCmsPageSection(sectionId, {
          module_type: moduleType,
          label,
          is_active: active,
          display_order: displayOrder,
          content,
        });
      } else {
        const created = await createCmsPageSection(page.id, {
          module_type: moduleType,
          section_key: `${moduleType}_${Date.now()}_${index}`,
          label,
          is_active: active,
          display_order: displayOrder,
          content,
        });
        if (created?.id) seenExistingIds.add(Number(created.id));
      }
    }

    for (const oldId of existingIds) {
      if (!seenExistingIds.has(oldId)) {
        await deleteCmsPageSection(oldId);
      }
    }
  };

  const handlePublish = async (data) => {
    if (!page) return;
    setSaving(true);
    setError('');
    try {
      await updateCmsPage(page.id, {
        title: page.title,
        slug: page.slug,
        status: page.status,
        seo_title: page.seo_title,
        seo_description: page.seo_description,
        og_title: page.og_title,
        og_description: page.og_description,
        og_image_url: page.og_image_url,
        canonical_url: page.canonical_url,
        robots: page.robots,
        schema_json: page.schema_json,
        show_header: page.show_header ? 1 : 0,
        show_footer: page.show_footer ? 1 : 0,
      });
      await syncSectionsFromPuck(data);
      setNotify({ open: true, message: 'Page builder saved successfully', severity: 'success' });
      await loadPage();
      if (previewOpen) setPreviewRefreshKey((key) => key + 1);
    } catch (err) {
      const message = err?.response?.data?.error || err?.response?.data?.details || err.message || 'Failed to save builder page';
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePage = async () => {
    if (!page || page.is_locked) return;
    // eslint-disable-next-line no-restricted-globals
    const confirmed = window.confirm(`Delete page "${page.title}"? This cannot be undone.`);
    if (!confirmed) return;
    try {
      await deleteCmsPage(page.id);
      enqueueSnackbar('Page deleted', { variant: 'success' });
      navigate(experiencePath('/website'));
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || 'Failed to delete page', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box className="website-puck-page" sx={{ display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!page) {
    return (
      <Box className="website-puck-page" sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Page not found'}</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ChevronLeftIcon />} onClick={() => navigate(experiencePath('/website'))}>Back to pages</Button>
      </Box>
    );
  }

  return (
    <Box className="website-puck-page">
      <Box className="website-puck-header">
        <Box sx={{ px: 2.5, py: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
              <CrmButton startIcon={<ChevronLeftIcon fontSize="small" />} onClick={() => navigate(experiencePath('/website'))} variant="secondary" size="small">
                Pages
              </CrmButton>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h6" fontWeight={900} noWrap>{page.title}</Typography>
                  {pageStatusChip}
                  {page.is_locked ? <Chip label="System page" size="small" /> : null}
                </Stack>
                <Typography variant="caption" color="text.secondary">/{page.slug === 'home' ? '' : page.slug} · {sections.length} saved sections · Puck visual editor</Typography>
              </Box>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <CrmButton onClick={openPreview} variant="secondary" size="small">
                Preview
              </CrmButton>
              <CrmButton startIcon={<SettingsIcon fontSize="small" />} onClick={() => setPageSettingsOpen((prev) => !prev)} size="small" variant={pageSettingsOpen ? 'primary' : 'secondary'}>
                Page Settings
              </CrmButton>
            </Stack>
          </Stack>
        </Box>

        <Accordion expanded={pageSettingsOpen} onChange={() => setPageSettingsOpen((prev) => !prev)} elevation={0} square>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ display: 'none' }} />
          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <CrmInput
                      label="Page title"
                      value={page.title || ''}
                      onChange={(event) => setPage((prev) => ({ ...prev, title: event.target.value, slug: prev.is_locked ? prev.slug : slugify(event.target.value) }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <CrmInput
                      label="Slug"
                      value={page.slug || ''}
                      onChange={(event) => setPage((prev) => ({ ...prev, slug: slugify(event.target.value) }))}
                      disabled={Boolean(page.is_locked)}
                      helperText={page.is_locked ? 'System page slug is locked' : 'Do not use shop/checkout/product slugs'}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <CrmSelect
                      label="Status"
                      value={page.status || 'draft'}
                      onChange={(event) => setPage((prev) => ({ ...prev, status: event.target.value }))}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </CrmSelect>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <CrmSwitch
                      checked={Boolean(page.show_header)} onChange={(event) => setPage((prev) => ({ ...prev, show_header: event.target.checked }))}
                      label="Header"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <CrmSwitch
                      checked={Boolean(page.show_footer)} onChange={(event) => setPage((prev) => ({ ...prev, show_footer: event.target.checked }))}
                      label="Footer"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CrmInput label="SEO title" value={page.seo_title || ''} onChange={(event) => setPage((prev) => ({ ...prev, seo_title: event.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CrmInput label="SEO description" value={page.seo_description || ''} onChange={(event) => setPage((prev) => ({ ...prev, seo_description: event.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CrmInput label="OG title" value={page.og_title || ''} onChange={(event) => setPage((prev) => ({ ...prev, og_title: event.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CrmInput label="OG description" value={page.og_description || ''} onChange={(event) => setPage((prev) => ({ ...prev, og_description: event.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MediaAssetPicker label="Social sharing image" currentUrl={page.og_image_url || ''} onChange={(_, asset) => setPage(prev => ({ ...prev, og_image_url: asset?.url || '' }))} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CrmInput label="Canonical URL" value={page.canonical_url || ''} onChange={(event) => setPage((prev) => ({ ...prev, canonical_url: event.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CrmInput label="Robots" value={page.robots || 'index, follow'} onChange={(event) => setPage((prev) => ({ ...prev, robots: event.target.value }))} />
                  </Grid>
                  <Grid item xs={12}>
                    <CrmTextarea label="Schema JSON" value={page.schema_json || ''} onChange={(event) => setPage((prev) => ({ ...prev, schema_json: event.target.value }))} rows={4} />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Click Publish inside the editor to save section/module changes. Use this button only for page title, slug and SEO changes.
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {!page.is_locked ? <CrmButton variant="danger" onClick={handleDeletePage}>Delete Page</CrmButton> : null}
                        <CrmButton startIcon={<SaveIcon fontSize="small" />} variant="primary" onClick={savePageSettings}>Save Page Settings</CrmButton>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </AccordionDetails>
        </Accordion>

        {error ? <Alert severity="error" sx={{ mx: 2.5, mb: 1 }}>{error}</Alert> : null}
        {saving ? <Alert severity="info" sx={{ mx: 2.5, mb: 1 }}>Saving page builder content…</Alert> : null}
      </Box>

      <Divider />

      <Box className="website-puck-editor">
        <Puck
          key={`${page.id}-${editorVersion}`}
          config={puckConfig}
          data={puckData}
          iframe={{ enabled: false }}
          overrides={puckOverrides}
          onChange={setPuckData}
          onPublish={handlePublish}
        />
      </Box>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullScreen>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#f1f5f9' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1, borderBottom: '1px solid #e2e8f0', bgcolor: '#fff' }}>
            <Typography variant="subtitle2" fontWeight={800}>Preview — {page.title}</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ToggleButtonGroup size="small" exclusive value={previewViewport} onChange={(_, next) => next && setPreviewViewport(next)}>
                <ToggleButton value="desktop"><DesktopWindowsIcon fontSize="small" /></ToggleButton>
                <ToggleButton value="tablet"><TabletMacIcon fontSize="small" /></ToggleButton>
                <ToggleButton value="mobile"><PhoneIphoneIcon fontSize="small" /></ToggleButton>
              </ToggleButtonGroup>
              <IconButton size="small" onClick={() => setPreviewRefreshKey((key) => key + 1)} title="Refresh preview">
                <RefreshIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setPreviewOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
          <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', p: 2 }}>
            {!previewFrameUrl ? (
              <Box sx={{ display: 'grid', placeItems: 'center', width: '100%' }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: viewportWidths[previewViewport], maxWidth: '100%', height: '100%', bgcolor: '#fff', boxShadow: previewViewport === 'desktop' ? 'none' : '0 0 0 1px #e2e8f0', transition: 'width 0.2s ease' }}>
                <iframe
                  id="preview-frame"
                  ref={previewFrameRef}
                  key={previewRefreshKey}
                  title="CMS page preview"
                  src={previewFrameUrl}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Dialog>

      <NotificationSnackbar
        open={notify.open}
        message={notify.message}
        severity={notify.severity}
        onClose={() => setNotify((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
}

export default PageBuilder;
